const MODEL_TOKEN_REGEX = /\b(?=[A-Z0-9._/-]*\d)[A-Z][A-Z0-9._/-]{3,}\b/g

const normalizeLine = (line = '') =>
  String(line)
    .replace(/\u00a0/g, ' ')
    .replace(/[ ]{2,}/g, ' ')
    .trim()

const cleanFieldValue = (value = '') =>
  (normalizeLine(value)
    .split(/\t+/)[0]
    || normalizeLine(value).split(/\t+/).map(normalizeLine).find(Boolean)
    || '')
    .replace(/\s*(Reason for purchase|Walk up\/Elevator|Date Required|Location|Quantity)\b.*$/i, '')
    .trim()

const extractLabeledValue = (text, patterns) => {
  for (const pattern of patterns) {
    const match = text.match(pattern)
    if (match?.[1]) return cleanFieldValue(match[1])
  }
  return ''
}

const parseMoney = (value) => {
  const cleaned = String(value || '').replace(/[^\d.]/g, '')
  if (!cleaned) return null
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : null
}

const extractPrice = (line = '') => {
  const matches = [...String(line).matchAll(/\$\s*([\d,]+(?:\.\d{1,2})?)/g)]
  if (!matches.length) return null
  return parseMoney(matches[matches.length - 1][1])
}

const extractQuantity = (line, cells) => {
  const startMatch = line.match(/^\s*(\d+)\s+[A-Z0-9._/-]{3,}/i)
  if (startMatch) return Number(startMatch[1])

  const quantityIndex = cells.findIndex((cell) => /^\d+$/.test(cell) && Number(cell) > 0 && Number(cell) < 1000)
  if (quantityIndex >= 0) return Number(cells[quantityIndex])

  return 1
}

const extractModel = (line = '') => {
  const candidates = String(line).toUpperCase().match(MODEL_TOKEN_REGEX) || []
  return candidates.find((candidate) => !['SUBTOTAL', 'APPLIANCES'].includes(candidate)) || ''
}

const extractDescription = (line, cells, model) => {
  if (!model) return ''
  const descriptionCell = cells.find((cell) => cell.toUpperCase().includes(model)) || line
  return normalizeLine(
    descriptionCell
      .replace(/^\d+\s+/, '')
      .replace(/\$\s*[\d,]+(?:\.\d{1,2})?/g, '')
      .replace(new RegExp(`\\b${model}\\b`, 'i'), '')
      .replace(/^[-: ]+/, '')
  )
}

const extractLocation = (cells, line) => {
  if (cells.length >= 3 && /^\d+$/.test(cells[1])) return cells[0]
  const aptMatch = line.match(/\b(?:APT\.?|UNIT|LOCATION)\s*[:#-]?\s*([A-Z0-9.-]+)/i)
  return aptMatch?.[0] || ''
}

const isHeaderLine = (line = '') =>
  /^(location|quantity|full description|category|unit price|price)$/i.test(normalizeLine(line))

const isNoiseLine = (line = '') => {
  const normalized = normalizeLine(line)
  if (!normalized) return true
  if (isHeaderLine(normalized)) return true
  if (/^subtotal\b/i.test(normalized)) return true
  if (/^#ref!?$/i.test(normalized)) return true
  if (/^\$\s*-?$/.test(normalized)) return true
  if (/^-\s*$/.test(normalized)) return true
  return false
}

const isQuantityLine = (line = '') => /^\d+$/.test(normalizeLine(line))

const isLocationLine = (line = '') =>
  /^(?:apt|apartment|unit|suite|ste|room|rm|location|loc)\.?\s*[:#-]?\s*[A-Z0-9][A-Z0-9 .-]*$/i.test(normalizeLine(line))

const findPreviousModelIndex = (lines, fromIndex) => {
  for (let index = fromIndex - 1; index >= 0; index -= 1) {
    if (extractModel(lines[index])) return index
  }
  return -1
}

const findNearbyCellBeforeModel = (lines, modelIndex, predicate) => {
  const previousModelIndex = findPreviousModelIndex(lines, modelIndex)
  for (let index = modelIndex - 1; index > previousModelIndex; index -= 1) {
    const line = lines[index]
    if (predicate(line)) return normalizeLine(line)
  }
  return ''
}

const hydrateItemFromVerticalTable = (item, lines, modelIndex) => {
  const nearbyLocation = item.location || findNearbyCellBeforeModel(lines, modelIndex, isLocationLine)
  const nearbyQuantity = findNearbyCellBeforeModel(lines, modelIndex, isQuantityLine)

  return {
    ...item,
    quantity: nearbyQuantity ? Number(nearbyQuantity) : item.quantity,
    location: nearbyLocation,
  }
}

const parseItemLine = (line) => {
  const normalized = normalizeLine(line)
  if (isNoiseLine(normalized)) return null

  const cells = String(line)
    .split(/\t+/)
    .map(normalizeLine)
    .filter(Boolean)
  const model = extractModel(normalized)
  if (!model) return null

  return {
    quantity: extractQuantity(normalized, cells),
    modelNumber: model,
    description: extractDescription(normalized, cells, model),
    pastedUnitPrice: extractPrice(normalized),
    location: extractLocation(cells, normalized),
    sourceLine: normalized,
  }
}

export const formatDateForInput = (date) => {
  if (!date || Number.isNaN(+date)) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const resolveDateText = (value, baseDate = new Date()) => {
  const text = normalizeLine(value).replace(/[.]+$/, '')
  if (!text || /^asap$/i.test(text)) return ''

  const explicitDate = new Date(text)
  if (!Number.isNaN(+explicitDate)) return formatDateForInput(explicitDate)

  const weekdays = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  }
  const weekdayName = Object.keys(weekdays).find((day) => new RegExp(`\\b${day}\\b`, 'i').test(text))
  if (!weekdayName) return ''

  const target = weekdays[weekdayName]
  const date = new Date(baseDate)
  date.setHours(0, 0, 0, 0)
  let daysAhead = (target - date.getDay() + 7) % 7
  if (daysAhead === 0) daysAhead = 7
  date.setDate(date.getDate() + daysAhead)
  return formatDateForInput(date)
}

export const parseQuotePaste = (rawText = '', baseDate = new Date()) => {
  const text = String(rawText || '').replace(/\r\n/g, '\n').replace(/\r/g, '\n')
  const lines = text.split('\n').map(normalizeLine).filter(Boolean)
  const deliveryNote = extractLabeledValue(text, [
    /Earliest\s+free\s+delivery\s+is\s+([^\n]+)/i,
    /Delivery\s*:?\s*([^\n]+)/i,
  ])
  const dateRequired = extractLabeledValue(text, [
    /Date\s+Required\s*:?\s*([^\n]+)/i,
    /Required\s+Date\s*:?\s*([^\n]+)/i,
  ])
  const dateAcquired = extractLabeledValue(text, [
    /Date\s+Acquired\s*:?\s*([^\n]+)/i,
    /Acquired\s+Date\s*:?\s*([^\n]+)/i,
  ])
  const reasonForPurchase = extractLabeledValue(text, [
    /Reason\s+for\s+purchase\s*:?\s*([^\n]+)/i,
  ])

  return {
    address: extractLabeledValue(text, [
      /Ship\s*to\s*Address\s*:?\s*([^\n]+)/i,
      /Ship\s*To\s*:?\s*([^\n]+)/i,
      /Address\s*:?\s*([^\n]+)/i,
    ]),
    dateAcquired,
    dateAcquiredInput: resolveDateText(dateAcquired, baseDate) || formatDateForInput(baseDate),
    dateRequired,
    dateRequiredInput: resolveDateText(dateRequired, baseDate) || resolveDateText(deliveryNote, baseDate),
    deliveryNote,
    reasonForPurchase,
    walkUp: extractLabeledValue(text, [
      /Walk\s*up\/?Elevator\s*:?\s*([^\n]+)/i,
      /Walk\s*up\s*:?\s*([^\n]+)/i,
      /Elevator\s*:?\s*([^\n]+)/i,
    ]),
    subtotal: parseMoney((text.match(/Subtotal\s*\$?\s*([\d,]+(?:\.\d{1,2})?)/i) || [])[1]),
    items: lines
      .map((line, index) => {
        const item = parseItemLine(line)
        return item ? hydrateItemFromVerticalTable(item, lines, index) : null
      })
      .filter(Boolean),
  }
}
